import React from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { bool, func, string } from 'prop-types';

import categoryProps from 'TMPropTypes/categoryProps';
import { CategoryLabels } from 'TMComponents/Typography/styles';

/**
 * Styling props are transient (`$`-prefixed).
 *
 * `Wrapper` renders as a gatsby `Link` when the pill is a link, and
 * styled-components stops filtering unknown props once `as` is a component -
 * so anything not prefixed ends up spread onto the `<a>` as an invalid DOM
 * attribute (`allowtoggle="true"`) with a React warning to match.
 */
const Wrapper = styled.div`
    ${CategoryLabels}
    align-items: center;
    background: ${({ $isActive, theme }) =>
        $isActive
            ? theme.colorSystem.primary[4]
            : theme.colorSystem.primary[3]};
    color: ${({ $isActive, theme }) =>
        $isActive
            ? theme.colorSystem.primary[3]
            : theme.colorSystem.primary[1]};
    border-radius: 30px;
    border: ${({ $isActive }) =>
        $isActive ? 'none' : '3px solid #E3E7F0'};
    display: inline-flex;
    justify-content: center;
    margin-right: 10px;
    padding: 0 32px;
    text-decoration: none;
    white-space: nowrap;

    ${({ $large, $allowToggle }) =>
        ($large || $allowToggle) &&
        `
        font-size: 0.875rem;
        font-weight: 700;
        line-height: 1.5rem;
        letter-spacing: -0.35px;
        padding: 8px 24px;
        height: auto;
    `};

    ${({ $allowToggle }) =>
        $allowToggle &&
        `
        cursor: pointer;
        margin-bottom: 10px;
        margin-top: 10px;
        transition: all 0.25s;
    `};
`;

/**
 * Pass `to` to render the pill as a link - the blog category filter uses this
 * so category pages are reachable by crawlers and shareable as URLs.
 */
const CategoryPill = ({
    name,
    to,
    large,
    allowToggle,
    isActive,
    onClick
}) => (
    <Wrapper
        {...(to ? { as: Link, to } : {})}
        $large={large}
        $allowToggle={allowToggle}
        $isActive={isActive}
        onClick={onClick}>
        {name}
    </Wrapper>
);

CategoryPill.propTypes = {
    to: string,
    large: bool,
    allowToggle: bool,
    isActive: bool,
    onClick: func,
    ...categoryProps
};

CategoryPill.defaultProps = {
    to: undefined,
    large: false,
    allowToggle: false,
    isActive: true,
    onClick: undefined
};

export default CategoryPill;
